/**
 * Live countdown to a match's lock time.
 *
 * Returns:
 *  - `locked: boolean` — whether the lock has passed
 *  - `text: string`    — human-readable pt-BR remaining time, e.g. "3h 12min"
 *  - `urgent: boolean` — true when less than 1 hour remains
 *
 * Re-renders at most once per minute (or per second when urgent).
 */

import { useEffect, useState } from 'react';
import { parseDateTimeInput } from '../lib/utils';

export interface LockCountdown {
  locked: boolean;
  text: string;
  urgent: boolean;
  totalSeconds: number;
}

const URGENT_THRESHOLD_SECONDS = 60 * 60; // 1h

export function formatLockRemaining(totalSeconds: number): string {
  if (totalSeconds <= 0) return 'Encerrado';
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${minutes}min`;
  if (minutes >= 1) return `${minutes}min`;
  return `${seconds}s`;
}

/**
 * Pure compute — exported for unit testing. Given the lock target as a
 * millisecond epoch and the current time, return the snapshot the hook
 * exposes. `targetMs <= 0` or `NaN` means "no lock" → fail closed (locked).
 */
export function computeLockCountdown(targetMs: number, nowMs: number): LockCountdown {
  if (!targetMs || Number.isNaN(targetMs)) {
    return { locked: true, text: 'Encerrado', urgent: false, totalSeconds: 0 };
  }
  const remaining = Math.max(0, Math.floor((targetMs - nowMs) / 1000));
  return {
    locked: remaining <= 0,
    text: formatLockRemaining(remaining),
    urgent: remaining > 0 && remaining < URGENT_THRESHOLD_SECONDS,
    totalSeconds: remaining,
  };
}

export function useLockCountdown(lockTime: string | Date | null | undefined): LockCountdown {
  const targetMs = lockTime ? parseDateTimeInput(lockTime).getTime() : 0;

  const compute = (): LockCountdown => computeLockCountdown(targetMs, Date.now());

  const [state, setState] = useState<LockCountdown>(compute);

  useEffect(() => {
    setState(compute());
    if (!targetMs) return undefined;

    let interval: ReturnType<typeof setInterval> | null = null;

    function schedule() {
      const next = compute();
      setState(next);
      if (next.locked) {
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
        return;
      }
      // Re-tick every second when urgent, every 30s otherwise.
      const period = next.urgent ? 1000 : 30000;
      if (interval) clearInterval(interval);
      interval = setInterval(schedule, period);
    }

    schedule();
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs]);

  return state;
}
